import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import i18n from "i18n";

import { StockService } from "services/StockService";
import { AuthService } from "services/AuthService";
import { OrderService } from "services/OrderService";
import { EventRepository } from "helpers/EventRepository";
import * as MobileHelpers from "helpers/Mobile";

import AdminLayout from "../../../../../../components/MainApp/layouts/DesktopLayout";
import ModalGuardProcessingInfo from "components/MainApp/organisms/Modals/ModalGuardProcessingInfo/ModalGuardProcessingInfo";
import ErrorModal from "components/MainApp/organisms/Stock/ErrorModalAuditory";
import { NotFoundItems } from "views/Admin/components/NotFoundItems";
import Loader from "components/MainApp/atoms/Loader";
import Tooltip from "components/MainApp/atoms/Tooltip";
import Search from "components/MainApp/atoms/Filters/Search";
import TwoOptionFilter from "components/MainApp/atoms/Filters/TwoOptionFilter";
import Checkbox from "components/Auth/atoms/forms/CheckboxCostum";
import Icon from "components/MainApp/atoms/Icon/icon";

import zoneIcon from "assets/images/zone-house-icon-blue.svg";
import calendarIcon from "assets/images/calendar-icon.svg";
import excel from "assets/images/excel.svg";
import excelwhite from "assets/images/excelwhite.svg";

import styles from "./ViewDetailsStockAudit.module.scss";

const ViewStockAuditDetails = () => {
  const { auditId } = useParams();
  const location = useLocation();
  const auditDataFromParent = location.state?.auditData || null;
  const [auditDetails, setAuditDetails] = useState({});
  const [auditMeta, setAuditMeta] = useState({
    path_name: "",
    date: null,
    status: undefined,
    business: null
  });
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  const [checkboxStates, setCheckboxStates] = useState({});
  const [showManualRedirect, setShowManualRedirect] = useState(false);
  const [showAutomaticProcessing, setShowAutomaticProcessing] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    headerText: "",
    bodyText: "",
    duration: 4000
  });
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedRfids, setSelectedRfids] = useState([]);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [foundFilter, setFoundFilter] = useState(null);
  const [isSelectedRfidVisible, setIsSelectedRfidVisible] = useState(true);
  const selectedRfidRef = useRef(null);
  const contentRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const selectionFooterRef = useRef(null);
  const isMobileDevice = MobileHelpers.isMobile;
  const BATCH_SIZE = isMobileDevice ? 15 : 50;
  const [renderedCount, setRenderedCount] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState("1");
  const [isFiltering, setIsFiltering] = useState(false);
  const canSkipSupervision = AuthService.getPermissions().includes(
    "skip_order_supervision"
  );
  const [errorDetails, setErrorDetails] = useState({
    inMovement: [],
    missing: []
  });
  const [showDetails, setShowDetails] = useState(false);

  const toggleProductExpanded = productId => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleCheckboxChange = (productId, rfid) => {
    setSelectedRfids(prev => {
      const isSelected = prev.some(item => item.rfid === rfid);
      if (isSelected) {
        return prev.filter(item => item.rfid !== rfid); // Remove if already present
      } else {
        return [...prev, { productId, rfid }]; // Add if it's not already there
      }
    });
  };

  const handleCloseRfidIndicator = () => {
    setSelectedRfids([]);
  };

  const productsToRender = useMemo(() => {
    const allProducts = Object.values(auditDetails || {});
    // If there are no audit details loaded, return an empty array.
    if (!allProducts.length && !loading) return [];

    const q = (searchQuery || "").toLowerCase().trim();

    return allProducts
      .map(p => {
        const rfidsFiltered =
          foundFilter === null
            ? p.rfids
            : p.rfids.filter(
                r => Boolean(r.found_status) === Boolean(foundFilter)
              );

        return { ...p, rfids: rfidsFiltered };
      })
      .filter(p => {
        // If there is no search query, we filter only for the existence of RFIDs (if the found/not found filter was used)
        if (!q) return p.rfids.length > 0 || foundFilter === null;

        // If there is a search query, we filter by name or SKU
        return (
          (p.product_name && p.product_name.toLowerCase().includes(q)) ||
          (p.sku &&
            String(p.sku)
              .toLowerCase()
              .includes(q))
        );
      });
  }, [auditDetails, searchQuery, foundFilter, loading]);

  const isExcelDisabled = loading || productsToRender.length === 0;

  useEffect(() => {
    if (
      selectedRfids.length === 0 ||
      !selectedRfidRef.current ||
      !contentRef.current
    )
      return;

    const handleScroll = () => {
      const scrollContainer = contentRef.current;
      const element = selectedRfidRef.current;

      if (!element || !scrollContainer) return;

      const scrollContainerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const isVisible =
        elementRect.top >= scrollContainerRect.top &&
        elementRect.bottom <= scrollContainerRect.bottom;

      setIsSelectedRfidVisible(isVisible);
    };

    const scrollContainer = contentRef.current;
    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [selectedRfids]);

  useEffect(() => {
    const fetchAuditDetails = async () => {
      try {
        const data = await StockService.getAuditDetails(auditId);

        if (auditDataFromParent) {
          setAuditMeta({
            path_name: auditDataFromParent.path_name || "",
            date: auditDataFromParent.date || null,
            status: auditDataFromParent.status || undefined,
            business: auditDataFromParent.business || null
          });
        } else if (Array.isArray(data) && data.length > 0) {
          const first = data[0];
          setAuditMeta({
            path_name: first.path_name || "",
            date: first.date || null,
            status: first.status
          });
        }

        const groupedData = data.reduce((acc, item) => {
          const { product_id } = item;
          if (!acc[product_id]) {
            acc[product_id] = {
              product_id,
              product_name: item.product_name,
              sku: item.sku,
              product_image: item.product_image,
              path_name: item.path_name,
              rfids: []
            };
          }
          acc[product_id].rfids.push(item);
          return acc;
        }, {});

        setAuditDetails(groupedData);

        const productsCount = Object.keys(groupedData).length;
        if (productsCount === 0) {
          setLoading(false);
        } else if (isMobileDevice) {
          const initialCount = Math.min(10, productsCount);
          setRenderedCount(initialCount);
        } else {
          setTimeout(() => setLoading(false), 50);
        }
      } catch (error) {
        setLoading(false);
      }
    };

    fetchAuditDetails();
  }, [auditId, auditDataFromParent]);

  useEffect(() => {
    if (isMobileDevice) {
      if (loading && renderedCount > 0) {
        setLoading(false);
      }

      if (!loading && renderedCount < productsToRender.length) {
        const timer = setTimeout(() => {
          setRenderedCount(prevCount =>
            Math.min(prevCount + BATCH_SIZE, productsToRender.length)
          );
        }, 50);

        return () => clearTimeout(timer);
      }
    }
  }, [loading, renderedCount, productsToRender.length, BATCH_SIZE]);

  useEffect(() => {
    if (selectedRfids.length > 0 && selectionFooterRef.current) {
      const height = selectionFooterRef.current.offsetHeight + 20;
      setFooterHeight(height);
    } else {
      setFooterHeight(0);
    }
  }, [selectedRfids.length]);

  const handleGenerateAutomaticOrder = async () => {
    // Prepare payload
    const itemsPayload = selectedRfids.map(sel => {
      const product = auditDetails[sel.productId];
      const rfidData = product.rfids.find(r => r.rfid === sel.rfid);
      return {
        audit_detail_id: rfidData.id,
        rfid: sel.rfid,
        product_id: sel.productId
      };
    });

    if (itemsPayload.length === 0) return;

    // Validate stock and existence of all tags in a single call
    const validated = await validateMultipleTags(
      selectedRfids.map(s => s.rfid)
    );
    if (!validated) return;

    setModalConfig({
      headerText: i18n.t("processing.theft.alert.header"),
      bodyText: i18n.t("modal.bodytext.automatic.order.audit", {
        count: itemsPayload.length
      })
    });
    setShowAutomaticProcessing(true);

    try {
      // BACKEND CALL: Generate the order and update audit details in parallel
      await StockService.generateAuditOrder({ items: itemsPayload });

      // Mass UI Update and Audit Completion Check
      // Transform the format that updateLocalAndServerAuditStatus expects
      const itemsToUpdate = itemsPayload.map(item => ({
        productId: item.product_id,
        rfid: item.rfid,
        auditDetailId: item.audit_detail_id
      }));

      await updateLocalAndServerAuditStatus(itemsToUpdate);

      // Cleaning and Notification
      setSelectedRfids([]);
      EventRepository.notificationSend({
        label: i18n.t("success.notification.automatic.audit.order"),
        type: "success"
      });
    } catch (error) {
      let finalMsg = i18n.t("error.notification.automatic.audit.order");

      // Attempt to capture backend error details
      if (error.response?.data?.detail) {
        finalMsg = error.response.data.detail;
      }

      setErrorMessage(finalMsg);
      setOpenErrorModal(true);
    } finally {
      setShowAutomaticProcessing(false);
    }
  };

  const updateLocalAndServerAuditStatus = async itemsToUpdate => {
    // Server: All promises in parallel
    await Promise.all(
      itemsToUpdate.map(item =>
        StockService.updateAuditDetail(auditId, item.auditDetailId, {
          found_status: true,
          modified_status: true
        })
      )
    );

    // Local: A single bulk update of auditDetails
    setAuditDetails(prevDetails => {
      const nextDetails = { ...prevDetails };

      itemsToUpdate.forEach(item => {
        const product = nextDetails[item.productId];
        if (product) {
          product.rfids = product.rfids.map(r =>
            r.rfid === item.rfid
              ? { ...r, found_status: true, modified_status: true }
              : r
          );
        }
      });

      // Verify if the audit was completed
      const allRfidFound = Object.values(nextDetails).every(p =>
        p.rfids.every(rfid => rfid.found_status === true)
      );

      if (allRfidFound) {
        StockService.updateAuditStatus(auditId, true);
        setAuditMeta(prev => ({ ...prev, status: true }));
      }

      return nextDetails;
    });
  };

  const validateMultipleTags = async rfidList => {
    try {
      // 1. Retrieve the node code from the user's active session
      const currentNode = AuthService.currentNodeValue();
      const currentUserNodeCode = currentNode?.code;

      // 2. Query backend for the current status of these tags (existence, location, movement)
      const itemsFromStock = await StockService.validate_tags(rfidList);

      // 3. Detect tags currently in transit (in movement)
      const inMovement = itemsFromStock
        .filter(i => i.status === "IN_MOV")
        .map(i => i.tag?.rfid);

      // 4. Detect tags located in a different node (Branch mismatch)
      const wrongNode = itemsFromStock
        .filter(i => {
          // Extract item node code (could be an object or string)
          const itemNodeCode = i.location?.node;
          return String(itemNodeCode) !== String(currentUserNodeCode);
        })
        .map(i => i.tag?.rfid);

      // 5. Detect tags available (Same node and status LOC)
      const availableRfids = itemsFromStock
        .filter(i => {
          const itemNodeCode = i.location?.node;
          return (
            i.status === "LOC" &&
            String(itemNodeCode) === String(currentUserNodeCode)
          );
        })
        .map(i => i.tag?.rfid);

      // 6. Detect missing or non-existent tags
      const missing = rfidList.filter(
        rfid =>
          !availableRfids.includes(rfid) &&
          !inMovement.includes(rfid) &&
          !wrongNode.includes(rfid)
      );

      // --- ERROR HANDLING ---
      if (wrongNode.length > 0 || inMovement.length > 0 || missing.length > 0) {
        setErrorDetails({ inMovement, missing, wrongNode });

        // PRIORITY: Branch mismatch (Security/Context error)
        if (wrongNode.length > 0) {
          const auditLocation =
            auditMeta.path_name?.split(" / ")[0]?.trim() ||
            i18n.t("audit.location");

          setErrorMessage(
            i18n.t("error.branch.mismatch", {
              count: wrongNode.length,
              location: auditLocation
            })
          );
        }
        else {
          setErrorMessage(i18n.t("tags.not.available.in.stock"));
        }

        setOpenErrorModal(true);
        return null;
      }

      // If validation passes, return only items ready for processing
      return itemsFromStock.filter(i => i.status === "LOC");
    } catch (error) {
      setErrorMessage(i18n.t("generic.error"));
      setErrorDetails({ inMovement: [], missing: [], wrongNode: [] }); // Clean details
      setOpenErrorModal(true);
      return null;
    }
  };

  const handleGenerateManualOrder = async () => {
    const rfidStrings = selectedRfids.map(item => item.rfid);

    if (rfidStrings.length === 0) return;

    // Validate existence and stock
    const validatedItems = await validateMultipleTags(rfidStrings);
    if (!validatedItems) return;

    // Validate location
    const tagsWithoutLocation = validatedItems.filter(
      item => !item.location || !item.location.path_name
    );

    if (tagsWithoutLocation.length > 0) {
      const missingNames = tagsWithoutLocation.map(i => i.tag?.rfid).join(", ");
      setErrorMessage(`${i18n.t("tags.not.located")}: ${missingNames}`);
      setOpenErrorModal(true);
      return;
    }

    // Start Loading UI
    setModalConfig({
      headerText: i18n.t("processing.theft.alert.header"),
      bodyText: i18n.t("modal.bodytext.manual.order.audit"),
      duration: 3000
    });
    setShowManualRedirect(true);

    try {
      const currentUser = AuthService.currentUserValue();
      const ownerId = currentUser?.account?.id;

      if (!ownerId) throw new Error(i18n.t("errors.no.owner.id"));

      const notes = `${i18n.t(
        "order.created.manually.note"
      )} | Auditoría ID: ${auditId} | Tags: ${rfidStrings.length}`;

      const payload = {
        type: "PRE",
        klass: "AUD",
        status: "DRA",
        owner: ownerId,
        notes: notes
      };

      // Create the order
      const order = await OrderService.createOrder(payload);

      // Cross-reference validatedItems with audit data to obtain audit_detail_id
      // updateLocalAndServerAuditStatus needs to know the audit ID.
      const auditDetailsArray = Object.values(auditDetails).flatMap(
        p => p.rfids
      );
      const rfidToAuditDetailMap = {};

      // Maps selected RFIDs to their Audit Detail IDs to update status only after order confirmation.
      selectedRfids.forEach(sel => {
        const match = auditDetailsArray.find(a => a.rfid === sel.rfid);
        if (match) {
          rfidToAuditDetailMap[sel.rfid] = match.id;
        }
      });

      // Redirection
      setTimeout(() => {
        setShowManualRedirect(false);
        setCheckboxStates([]);

        history.push({
          pathname: `/admin/orders/preparation/edit/${order.id}`,
          state: {
            preloadedTags: rfidStrings,
            isFromAudit: true,
            auditId: auditId,
            auditDetailMap: rfidToAuditDetailMap
          }
        });
      }, 4000);
    } catch (err) {
      setShowManualRedirect(false);
      setErrorMessage(i18n.t("generic.error"));
      setOpenErrorModal(true);
    }
  };

  if (!loading && Object.keys(auditDetails).length === 0) {
    return <NotFoundItems />;
  }

  const formatDate = d => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      return dt.toLocaleString();
    } catch (e) {
      return String(d);
    }
  };

  const renderStatusPill = status => {
    const isComplete =
      status === "complete" || status === "COMPLETO" || status === true;
    return (
      <div
        className={`${styles["status-pill"]} ${
          isComplete ? styles["complete"] : styles["incomplete"]
        }`}
      >
        {isComplete
          ? i18n?.t?.("complete") || "COMPLETO"
          : i18n?.t?.("incomplete") || "INCOMPLETO"}
      </div>
    );
  };

  const getSelectedProductName = () => {
    if (selectedRfids.length === 0) return "";
    
    if (selectedRfids.length === 1) {
      const selection = selectedRfids[0];
      const product = auditDetails[selection.productId];
      return product?.product_name || i18n.t("unknown.product");
    }
    
    return `${selectedRfids.length} ${i18n.t("selected.tags")}`;
  };

  const downloadReport = async () => {
    if (loading) return;
    try {
      const blob = await StockService.downloadAuditDetailsExcel(auditId);

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const dateStr = new Date().toISOString().slice(0, 10);
      const fileNameBase = i18n.t("audit.detail.file");
      link.setAttribute("download", `${fileNameBase}_ID:${auditId}_${dateStr}.xlsx`);

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      EventRepository.notificationSend({
        label: i18n.t("success.download"),
        type: "success"
      });
    } catch (error) {
      EventRepository.notificationSend({
        label: i18n.t("error.downloading.report"),
        type: "error"
      });
    }
  };

  const msgTooltip = i18n.t("download.excel.report");

  const headerButtons = (
    <Tooltip
      content={
        <div
          className={styles["excel-button"]}
          onClick={isExcelDisabled ? null : downloadReport}
          style={{
            width: "30px",
            cursor: isExcelDisabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            opacity: isExcelDisabled ? "0.4" : "1",
            transition: "opacity 0.3s ease"
          }}
        >
          <img
            style={{ height: "30px" }}
            src={isMobileDevice ? excelwhite : excel}
            alt="Excel Icon"
          />
        </div>
      }
      text={isExcelDisabled ? i18n.t("no.data.to.export") : msgTooltip}
    />
  );

  useEffect(() => {
    if (searchQuery || foundFilter !== null) {
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, foundFilter]);

  return (
    <div className={styles["view-audit-details-container"]}>
      <AdminLayout
        headerTitle={i18n.t("audit.details")}
        headerOptions={[]}
        headerOptionsIcons={[]}
        customButtons={headerButtons}
        content={
          <div
            className={styles["audit-details-content"]}
            ref={contentRef}
            style={{
              paddingBottom: `${footerHeight > 0 ? footerHeight : 20}px`
            }}
          >
            {/* TOP CARD: search + found filter / meta row */}
            <div className={styles["top-card"]}>
              <div className={styles["top-card-row"]}>
                <div className={styles["top-card-search"]}>
                  <Search
                    resultList={[]}
                    placeholder={i18n.t("search.by.name.or.code")}
                    value={searchQuery}
                    handleSubmit={val => setSearchQuery(val)}
                    handleSubmitKey={payload => {
                      if (payload && payload.label) {
                        setSearchQuery(payload.label);
                      }
                    }}
                    filterProducts={val => Promise.resolve([])}
                  />
                </div>
                <div className={styles["top-card-filter"]}>
                  <TwoOptionFilter
                    options={[
                      { label: i18n.t("found"), value: true },
                      { label: i18n.t("not.found"), value: false }
                    ]}
                    selectedOptions={
                      foundFilter === null
                        ? []
                        : [
                            {
                              label: foundFilter
                                ? i18n.t("found")
                                : i18n.t("not.found"),
                              value: foundFilter
                            }
                          ]
                    }
                    handleSelection={(option, operation) => {
                      if (operation === "deleteAll") {
                        setFoundFilter(null);
                        return;
                      }
                      if (operation === "add") {
                        setFoundFilter(option.value);
                      } else if (operation === "delete") {
                        setFoundFilter(null);
                      }
                    }}
                    firstOptionLabel="all"
                  />
                </div>
              </div>

              <div className={styles["top-card-row-meta"]}>
                <div className={styles["meta-item"]}>
                  <div className={styles["icon-wrapper"]}>
                    <div className={styles["icon-bg"]} />
                    <img
                      src={zoneIcon}
                      alt="zone"
                      className={styles["meta-icon"]}
                    />
                  </div>
                  <div>
                    <p>{auditMeta.path_name}</p>
                  </div>
                </div>
                <div className={styles["meta-item"]}>
                  <div className={styles["icon-wrapper"]}>
                    <div className={styles["icon-bg"]} />
                    <img
                      src={calendarIcon}
                      alt="date"
                      className={styles["meta-icon"]}
                      style={{ width: "26px", height: "26px" }}
                    />
                  </div>
                  <div>
                    <p>{formatDate(auditMeta.date)}</p>
                  </div>
                </div>
                <div className={styles["meta-status"]}>
                  {renderStatusPill(auditMeta.status)}
                </div>
              </div>
            </div>

            <div className={styles["products-container"]}>
              {loading || isFiltering ? (
                <Loader />
              ) : productsToRender.length > 0 ? (
                (isMobileDevice
                  ? productsToRender.slice(0, renderedCount)
                  : productsToRender
                ).map(product => {
                  const totalRfidCount = product.rfids.length;
                  const foundCount = product.rfids.filter(
                    rfid => rfid.found_status
                  ).length;
                  const isExpanded =
                    expandedProducts[product.product_id] || false;

                  return (
                    <div
                      key={product.product_id}
                      className={styles["product-card"]}
                    >
                      <div
                        className={styles["product-card-header"]}
                        onClick={() =>
                          toggleProductExpanded(product.product_id)
                        }
                      >
                        <div className={styles["product-header-left"]}>
                          <img
                            src={product.product_image}
                            alt={product.product_name}
                            className={styles["product-image"]}
                          />
                          <div className={styles["product-header-texts"]}>
                            <h3
                              className={styles["product-name"]}
                              title={product.product_name}
                            >
                              {product.product_name}
                            </h3>
                            <p
                              className={styles["product-sku"]}
                              title={product.sku}
                            >
                              {product.sku}
                            </p>
                            <p
                              className={styles["product-path"]}
                              title={product.path_name}
                            >
                              {product.path_name}
                            </p>
                          </div>
                        </div>
                        <div className={styles["product-header-right"]}>
                          <p>
                            {foundCount}/{totalRfidCount}
                          </p>
                          {totalRfidCount === 0 ? (
                            <span
                              className={
                                styles["icons"] + " " + styles["question_blue"]
                              }
                            >
                              ?
                            </span>
                          ) : foundCount === totalRfidCount ? (
                            <i
                              className={` ${styles["icons"]} ${styles["icon-check"]} icon-check`}
                            ></i>
                          ) : foundCount > 0 && foundCount < totalRfidCount ? (
                            <i
                              className={` ${styles["icons"]} ${styles["icon-iconos_menos"]} icon-iconos_menos`}
                            ></i>
                          ) : (
                            <i
                              className={` ${styles["icons"]} icon-cruz ${styles["icon-cruz"]}`}
                            ></i>
                          )}
                          <div
                            className={
                              styles["product-expand-button"] +
                              (isExpanded ? " " + styles["opened"] : "")
                            }
                            style={{ cursor: "pointer", marginLeft: "15px" }}
                          >
                            <i className="icon-chevron"></i>
                          </div>
                        </div>
                      </div>

                      <div
                        className={
                          styles["product-card-body"] +
                          (isExpanded ? " " + styles["opened"] : "")
                        }
                      >
                        {(!isMobileDevice || isExpanded) && (
                          <table className={styles["rfid-table"]}>
                            <tbody>
                              {product.rfids.map(rfid => {
                                const isSelectedThisRfid =
                                  selectedRfids.length > 0 &&
                                  selectedRfids[selectedRfids.length - 1]
                                    .rfid === rfid.rfid;

                                const isChecked =
                                  checkboxStates[product.product_id]?.[
                                    rfid.rfid
                                  ] || false;

                                const rfidTitle =
                                  `${i18n.t("product.name")}: ${
                                    product.product_name
                                  } | ` +
                                  `SKU: ${product.sku} | ` +
                                  `RFID: ${rfid.rfid} | ` +
                                  `${i18n.t("status")}: ${
                                    rfid.found_status
                                      ? i18n.t("found")
                                      : i18n.t("not.found")
                                  }`;

                                return (
                                  <tr
                                    key={rfid.rfid}
                                    ref={
                                      isSelectedThisRfid
                                        ? selectedRfidRef
                                        : null
                                    }
                                    title={rfidTitle}
                                  >
                                    <td>
                                      {!rfid.found_status && (
                                        <Checkbox
                                          checked={selectedRfids.some(
                                            item => item.rfid === rfid.rfid
                                          )}
                                          handleChange={() =>
                                            handleCheckboxChange(
                                              product.product_id,
                                              rfid.rfid
                                            )
                                          }
                                        />
                                      )}
                                    </td>
                                    <td>{rfid.rfid}</td>
                                    <td className={styles["icon-cell"]}>
                                      {rfid.found_status ? (
                                        <Icon
                                          name="check"
                                          width={20}
                                          height={20}
                                          fill={"#62d862"}
                                        />
                                      ) : (
                                        <Icon
                                          name="close"
                                          width={20}
                                          height={20}
                                          fill={"red"}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <NotFoundItems />
              )}
            </div>

            {selectedRfids.length > 0 && (
              <>
                {!isSelectedRfidVisible && (
                  <div className={styles["sticky-rfid-indicator"]}>
                    <div className={styles["indicator-content"]}>
                      <div className={styles["indicator-product"]}>
                        {getSelectedProductName()}
                      </div>
                      {selectedRfids.length === 1 && (
                        <div className={styles["indicator-rfid"]}>
                          TAG: {selectedRfids[0].rfid}
                        </div>
                      )}
                    </div>
                    <div
                      className={styles["close-indicator-button"]}
                      onClick={handleCloseRfidIndicator}
                    >
                      <i className="icon-cruz"></i>
                    </div>
                  </div>
                )}
                <div
                  className={styles["selection-footer"]}
                  ref={selectionFooterRef}
                >
                  <div className={styles["footer-inner"]}>
                    <div
                      className={`${styles["footer-button"]} ${styles["primary"]}`}
                      onClick={handleGenerateAutomaticOrder}
                    >
                      <div>
                        {i18n.t("generate.automatic.order").toUpperCase()}
                      </div>
                    </div>

                    {canSkipSupervision && (
                      <div
                        className={`${styles["footer-button"]} ${styles["secondary"]}`}
                        onClick={handleGenerateManualOrder}
                      >
                        <div>
                          {i18n.t("generate.manual.order").toUpperCase()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <ModalGuardProcessingInfo
              open={showAutomaticProcessing}
              headerText={modalConfig.headerText}
              bodyText={modalConfig.bodyText}
            />
            <ErrorModal
              open={openErrorModal}
              message={
                /* Sending JSX instead of a string */
                <div style={{ textAlign: "left", width: "100%" }}>
                  {/* Main error text (if exists) */}
                  {errorMessage && (
                    <div
                      style={{
                        textAlign: "center",
                        marginBottom: "10px",
                        fontFamily: "Ubuntu-Bold"
                      }}
                    >
                      {errorMessage}
                    </div>
                  )}

                  {/* Summary of counters */}
                  <div
                    style={{
                      fontSize: "16px",
                      marginBottom: "8px",
                      color: "#494f66"
                    }}
                  >
                    {errorDetails.inMovement.length > 0 && (
                      <p style={{ margin: 0 }}>
                        • {errorDetails.inMovement.length}{" "}
                        {i18n.t("tags.in.process")}
                      </p>
                    )}
                    {errorDetails.missing.length > 0 && (
                      <p style={{ margin: 0 }}>
                        • {errorDetails.missing.length}{" "}
                        {i18n.t("tags.not.found.in.stock")}
                      </p>
                    )}
                  </div>

                  {/* View Details button */}
                  {(errorDetails.inMovement.length > 0 ||
                    errorDetails.missing.length > 0) && (
                    <div style={{ textAlign: "center" }}>
                      <span
                        onClick={e => {
                          e.stopPropagation();
                          setShowDetails(!showDetails);
                        }}
                        style={{
                          fontSize: "14px",
                          color: "#3c7af5",
                          cursor: "pointer",
                          fontFamily: "Ubuntu-Medium",
                          textDecoration: "underline"
                        }}
                      >
                        {showDetails
                          ? i18n.t("hide.details")
                          : i18n.t("view.details")}
                      </span>
                    </div>
                  )}

                  {/* Scrollable List */}
                  {showDetails && (
                    <div
                      style={{
                        marginTop: "12px",
                        maxHeight: "150px",
                        overflowY: "auto",
                        border: "1px solid #e1e4e8",
                        padding: "10px",
                        backgroundColor: "#F5F8FE",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontFamily: "Ubuntu",
                        lineHeight: "1.6",
                        letterSpacing: "0.5px"
                      }}
                    >
                      {errorDetails.inMovement.length > 0 && (
                        <div style={{ marginBottom: "10px" }}>
                          <strong
                            style={{
                              color: "#856404",
                              display: "block",
                              fontFamily: "Ubuntu-Bold"
                            }}
                          >
                            {i18n.t("tags.in.order.in.process")}:
                          </strong>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              marginTop: "4px"
                            }}
                          >
                            {errorDetails.inMovement.map(rfid => (
                              <span
                                key={rfid}
                                style={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #dcdfe6",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "11px",
                                  color: "#666"
                                }}
                              >
                                {rfid}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {errorDetails.missing.length > 0 && (
                        <div>
                          <strong
                            style={{
                              color: "#721c24",
                              display: "block",
                              fontFamily: "Ubuntu-Bold"
                            }}
                          >
                            {i18n.t("tags.not.found")}:
                          </strong>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              marginTop: "4px"
                            }}
                          >
                            {errorDetails.missing.map(rfid => (
                              <span
                                key={rfid}
                                style={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #dcdfe6",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "11px",
                                  color: "#666"
                                }}
                              >
                                {rfid}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              }
              handleClose={() => setOpenErrorModal(false)}
              handleGoBack={() => setOpenErrorModal(false)}
              handleButtonClick={() => setOpenErrorModal(false)}
            />
            <ModalGuardProcessingInfo
              open={showManualRedirect}
              headerText={modalConfig.headerText}
              bodyText={modalConfig.bodyText}
            />
          </div>
        }
        navHidden={false}
        goBackFunc={() => history.push("/admin/stock/view-audits")}
      />
    </div>
  );
};

export default ViewStockAuditDetails;
